import { Injectable } from "@angular/core";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { ProgressCategory, ProgressCategoryBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem, ProgressItemBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressItemStatus, ProgressItemStatusMetadata } from "@common/ADAPT.Common.Model/organisation/progress-item-status";
import { ProgressProduct, ProgressProductBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { forkJoin, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class ProgressService {
    constructor(private commonDataService: CommonDataService) {}

    public static getTooltipTextForItems(items: ProgressItem[]) {
        const messages: string[] = [];

        const itemCount = items.length;
        if (itemCount === 0) {
            return `No tasks have been defined yet.`;
        }

        const statuses = [ProgressItemStatus.Done, ProgressItemStatus.InProgress, ProgressItemStatus.ToDo];
        for (const status of statuses) {
            const meta = ProgressItemStatusMetadata.ByStatus[status];
            const count = items.filter((item) => item.status === status).length;
            const icon = `<i class="fas fa-circle me-1" style="color: ${meta.colour}"></i>`;
            messages.push(`${icon} ${meta.name} ${count}/${itemCount} - ${Math.round((count / itemCount) * 100)}%`);
        }

        return `<div class="text-start">${messages.join("<br>")}</div>`;
    }

    public static getProgressPercent(items: ProgressItem[]) {
        const itemCount = items.length;
        if (itemCount === 0) {
            return 0;
        }

        const doneItems = items.filter((item) => item.status === ProgressItemStatus.Done).length;
        return Math.round((doneItems / itemCount) * 100);
    }

    public getTemplateProgressProducts() {
        return this.getProgressProducts();
    }

    public getProgressProducts(organisationId?: number) {
        const predicate = new MethodologyPredicate<ProgressProduct>("organisationId", "==", organisationId ?? null);
        return this.commonDataService.getByPredicate(ProgressProductBreezeModel, predicate);
    }

    public cloneProgressProduct(progressProduct: ProgressProduct, organisation: Organisation, ordinal: number) {
        return this.commonDataService.create(ProgressProductBreezeModel, {
            organisationId: organisation.organisationId,
            name: progressProduct.name,
            code: progressProduct.code,
            ordinal,
        }).pipe(
            switchMap((newProduct) => {
                if (progressProduct.progressCategories.length === 0) {
                    return of(newProduct);
                }

                return forkJoin(progressProduct.progressCategories.map((category) => this.cloneProgressCategory(category, newProduct))).pipe(
                    map(() => newProduct),
                );
            }),
        );
    }

    private cloneProgressCategory(progressCategory: ProgressCategory, destProgressProduct: ProgressProduct) {
        return this.commonDataService.create(ProgressCategoryBreezeModel, {
            progressProduct: destProgressProduct,
            name: progressCategory.name,
            ordinal: progressCategory.ordinal,
        }).pipe(
            switchMap((newCategory) => {
                if (progressCategory.progressItems.length === 0) {
                    return of(newCategory);
                }

                return forkJoin(progressCategory.progressItems.map((item) => this.cloneProgressItem(item, newCategory))).pipe(
                    map(() => newCategory),
                );
            }),
        );
    }

    private cloneProgressItem(progressItem: ProgressItem, destProgressCategory: ProgressCategory) {
        return this.commonDataService.create(ProgressItemBreezeModel, {
            progressCategory: destProgressCategory,
            name: progressItem.name,
            ordinal: progressItem.ordinal,
            status: progressItem.status,
        });
    }
}
